import getLogger from '@ignitesales/logger-es';
import { triggerIgniteAction as actionCompleted, flatProductList, ignite } from "@ignitesales/neuro-application";
import filterCartProductsByRecs from "../utils/filterCartProductsByRecs";
import buildHubspotFormBody from "../utils/hubspot/buildHubspotFormBody";
import { canTriggerHubspotPostApi, hubspotEndpoints } from "../utils/hubspot/buildHubspotUrl";
import { salesforceServiceMethods } from "../utils/salesforce/saveSalesforceParameters";

const logger = getLogger({ module: 'integration-action-creators' });

const hubspotLeadTransmittedAppkey = 'hubspotLeadTransmitted';
const salesforceLeadTransmittedAppkey = 'salesforceLeadTransmitted';

const IntegrationActions = {
  EXISTING_PRODUCTS_REQUEST_STARTED: 'EXISTING_PRODUCTS_REQUEST_STARTED',
  EXISTING_PRODUCTS_REQUEST_COMPLETE: 'EXISTING_PRODUCTS_REQUEST_COMPLETE',
  EXISTING_PRODUCTS_REQUEST_FAILED: 'EXISTING_PRODUCTS_REQUEST_FAILED',
  HUBSPOT_SEND_REQUEST_STARTED: 'HUBSPOT_SEND_REQUEST_STARTED',
  HUBSPOT_SEND_REQUEST_FAILED: 'HUBSPOT_SEND_REQUEST_FAILED',
  HUBSPOT_SEND_REQUEST_SUCCESSFUL: 'HUBSPOT_SEND_REQUEST_SUCCESSFUL',
  SALESFORCE_REQUEST_STARTED: 'SALESFORCE_REQUEST_STARTED',
  SALESFORCE_REQUEST_COMPLETE: 'SALESFORCE_REQUEST_COMPLETE',
  SALESFORCE_REQUEST_FAILED: 'SALESFORCE_REQUEST_FAILED'
};

const sendFormToHubspot = ({ lead, guideId }) => {
  return async (dispatch, getState) => {
    const currentState = getState();
    const user = currentState.user;
    const answers = currentState.answers;
    const products = currentState.products;
    const productCart = currentState.productCart;
    const cartProducts = filterCartProductsByRecs(Array.isArray(productCart) ? new Set(productCart) : productCart, products);
    const recommendedProducts = flatProductList(products);

    dispatch(triggerAction(IntegrationActions.HUBSPOT_SEND_REQUEST_STARTED));
    try {
      dispatch( saveLeadContactSubmissionAnswers() );
      const formBody = buildHubspotFormBody({
        answers,
        user,
        cartProducts,
        recommendedProducts,
        guideId,
        ...lead
      });
      const formApi = ignite.getRunMode() !== 'production' ? hubspotEndpoints.formApiTraining : hubspotEndpoints.formApi;

      if (canTriggerHubspotPostApi(ignite.getRunMode())) {
        logger.log('Preparing to call HubSpot');

        const response = await fetch(formApi, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formBody)
        });
        logger.info('Hubspot request sent', response.json());
      }

      await ignite.answerService.saveAnswer({
        appkey: hubspotLeadTransmittedAppkey,
        value: true,
        timestamp: new Date()
      });
      dispatch(triggerAction(IntegrationActions.HUBSPOT_SEND_REQUEST_SUCCESSFUL));

    } catch (e) {
      logger.error('Hubspot call failed', e);
      dispatch(requestFailed(IntegrationActions.HUBSPOT_SEND_REQUEST_FAILED, e));
    }
  }
};

const sendLeadToSalesforce = () => {
  return async (dispatch, getState) => {
    const currentState = getState();
    const user = currentState.user;
    const answers = currentState.answers;

    const salesforceAccountId = answers.salesforceAccountId && answers.salesforceAccountId.value ? answers.salesforceAccountId.value[0] : null;
    const redirectUrl = () => {
      if (salesforceAccountId && ignite.getRunMode() == 'staging') { // QA
        return `https://hbhc--sit.lightning.force.com/lightning/r/Account/${salesforceAccountId}/related/Opportunities/view`
      }
      else if (salesforceAccountId  && ignite.getRunMode() == 'pilot') { // training/production-staging
        return `https://hbhc--uat.sandbox.lightning.force.com/lightning/r/Account/${salesforceAccountId}/related/Opportunities/view`
      }
      else if (salesforceAccountId) {
        return `https://hbhc.lightning.force.com/lightning/r/${salesforceAccountId}/related/Opportunities/view`
      }
      else {
        return "https://hbhc.lightning.force.com"
      }
    }

    dispatch(triggerAction(IntegrationActions.SALESFORCE_REQUEST_STARTED));
    try {
      dispatch( saveLeadContactSubmissionAnswers() );
      logger.info('Attempting to send user information to salesforce');
      const response = await fetch(`/hancock/salesforce?method=${salesforceServiceMethods.sendLead}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/xml'
        },
        body: `<lead><uuid>${user.uuid}</uuid></lead>`
      });
      const jsonResponse = await response.json();
      logger.info('Salesforce call has finished', jsonResponse);
      window.open(redirectUrl(), '_blank');

      await ignite.answerService.saveAnswer({
        appkey: salesforceLeadTransmittedAppkey,
        value: true,
        timestamp: new Date()
      });
      dispatch(triggerAction(IntegrationActions.SALESFORCE_REQUEST_COMPLETE))
    } catch (e) {
      logger.error('Salesforce call failed', e);
      dispatch(requestFailed(IntegrationActions.SALESFORCE_REQUEST_FAILED))
    }
  }
};

const processExistingProducts = () => {
  return async (dispatch, getState) => {
    const currentState = getState();
    const user = currentState.user;
    const answers = currentState.answers;
    const customerLookupId = answers.rmNumberParams && answers.rmNumberParams.value ? answers.rmNumberParams.value[0] : null;

    if (customerLookupId) {
      dispatch(triggerAction(IntegrationActions.EXISTING_PRODUCTS_REQUEST_STARTED));
      try {
        logger.info('Attempting to process existing products');
        const response = await fetch(`/hancock/salesforce?method=${salesforceServiceMethods.updateProducts}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/xml'
          },
          body: `<lead><uuid>${user.uuid}</uuid></lead>`
        });
        const jsonResponse = await response.json();
        const responseValue = jsonResponse && jsonResponse.existingCustomerProducts ? jsonResponse.existingCustomerProducts : '';
        logger.info('Existing products call has finished, saving answers', jsonResponse);
        await ignite.answerService.saveAnswer({
          appkey: 'existingCustomerProducts',
          value: responseValue,
          timestamp: new Date()
        });
        dispatch(requestSuccessful(IntegrationActions.EXISTING_PRODUCTS_REQUEST_COMPLETE, responseValue))
      } catch (e) {
        logger.error('Existing products call failed', e);
        dispatch(requestFailed(IntegrationActions.EXISTING_PRODUCTS_REQUEST_FAILED))
      }
    } else {
      logger.info('No salesforce account ID associated with this conversation, skipping existing products call');
    }
  }
};

const saveLeadContactSubmissionAnswers = () => {
  return async dispatch => {
    try {
      dispatch(actionCompleted({actionName: 'contactMeClicked'}));
      await ignite.answerService.saveAnswer({
        appkey: 'leadTransmitted',
        value: 'contact',
        timestamp: new Date()
      })
    } catch (e) {
      logger.error('Unable to save lead / contact submission answers', e);
    }
  }
}

const triggerAction = (type) => {
  return {
    type
  };
};

const requestFailed = (type, error) => {
  return {
    type,
    error
  };
};

const requestSuccessful = (type, data) => {
  return {
    type,
    data
  };
};

export {
  hubspotLeadTransmittedAppkey, IntegrationActions, processExistingProducts, salesforceLeadTransmittedAppkey, sendFormToHubspot,
  sendLeadToSalesforce
};

